import { useCallback, useEffect, useState } from 'react';
import { EvidenciaImg } from '../../../../types/ot.types';
import { createInitialFormData, defaultExpediente, defaultHorarios } from '../constants/centro.constants';
import { CentroService } from '../services/CentroService';
import {
    CatalogoItem,
    CategoriaOption,
    CentroAlert,
    CentroFormData,
    CentroTabId,
    CiudadOption,
    EstadoOption,
    ExpedienteCentro,
    HorarioDia,
    ProveedorOption,
    Slot,
    TipoCentroOption,
    MarcaOption,
} from '../types/centro.types';
import { useCentroCatalogos } from './useCentroCatalogos';
import { useCentroMap } from './useCentroMap';

function decodeProveedorId(encoded: string | null): string {
    // idProveedor en URL suele venir en base64; si falla atob, usar el valor tal cual
    if (!encoded) return '';
    try {
        return atob(encoded);
    } catch {
        return encoded;
    }
}

export function useCentroForm() {
    const urlParams = new URLSearchParams(window.location.search);
    const centroIdEnc = urlParams.get('id');
    const idProveedorEnc = urlParams.get('idProveedor');
    const preProveedorId = decodeProveedorId(idProveedorEnc);
    const isEditMode = Boolean(centroIdEnc);

    const [activeTab, setActiveTab] = useState<CentroTabId>('atencion');
    const [isLoading, setIsLoading] = useState(isEditMode);
    const [isSaving, setIsSaving] = useState(false);
    const [alert, setAlert] = useState<CentroAlert | null>(null);
    const [formData, setFormData] = useState<CentroFormData>(() => createInitialFormData(preProveedorId));

    const [constanciaFiles, setConstanciaFiles] = useState<EvidenciaImg[]>([]);
    const [caratulaFiles, setCaratulaFiles] = useState<EvidenciaImg[]>([]);
    const [slots, setSlots] = useState<Slot[]>([]);

    const [proveedores, setProveedores] = useState<ProveedorOption[]>([]);
    const [estados, setEstados] = useState<EstadoOption[]>([]);
    const [ciudades, setCiudades] = useState<CiudadOption[]>([]);
    const [tiposCentro, setTiposCentro] = useState<TipoCentroOption[]>([]);
    const [categorias, setCategorias] = useState<CategoriaOption[]>([]);
    const [marcas, setMarcas] = useState<MarcaOption[]>([]);
    const [bancos, setBancos] = useState<CatalogoItem[]>([]);

    const [nuevoEstatus, setNuevoEstatus] = useState('');
    const [comentarioEstatus, setComentarioEstatus] = useState('');
    const [isChangingEstatus, setIsChangingEstatus] = useState(false);

    const catalogos = useCentroCatalogos(formData, setFormData, setAlert, categorias, marcas);

    const handleCoordsChange = useCallback((lat: string, lng: string) => {
        setFormData((prev) => ({ ...prev, lat, lng }));
    }, []);

    const { mapRef, searchInputRef } = useCentroMap({
        activeTab,
        lat: formData.lat,
        lng: formData.lng,
        radio: formData.radio,
        onCoordsChange: handleCoordsChange,
    });

    const loadCentroData = useCallback(async () => {
        if (!centroIdEnc) return;
        setIsLoading(true);
        try {
            const idProvEnc = idProveedorEnc ? btoa(preProveedorId) : '';
            const data = await CentroService.obtenerCentro(centroIdEnc, idProvEnc);
            if (data.status === 'success' && data.data) {
                const c = data.data;
                setFormData({
                    ...c,
                    banco: c.banco ?? '',
                    clabe: c.clabe ?? '',
                    horarios: c.horarios?.length ? c.horarios : defaultHorarios,
                    servicios: c.servicios ?? [],
                    expediente: c.expediente ?? defaultExpediente,
                    asignacion_automatica: Boolean(c.asignacion_automatica),
                });
                setNuevoEstatus(c.clave_estatus);
                if (c.slots) setSlots(c.slots);

                if (c.constancia_fiscal_url) {
                    setConstanciaFiles([
                        {
                            id: 'db_const',
                            url: c.constancia_fiscal_url,
                            tipo: 'pdf',
                            nombreOriginal: 'Constancia de Situación Fiscal.pdf',
                            file: undefined as unknown as File,
                        },
                    ]);
                }
                if (c.caratula_estado_cuenta_url) {
                    setCaratulaFiles([
                        {
                            id: 'db_carat',
                            url: c.caratula_estado_cuenta_url,
                            tipo: 'pdf',
                            nombreOriginal: 'Carátula de Estado de Cuenta.pdf',
                            file: undefined as unknown as File,
                        },
                    ]);
                }
            }
        } catch {
            setAlert({ type: 'danger', message: 'Error cargando datos.' });
        } finally {
            setIsLoading(false);
        }
    }, [centroIdEnc, idProveedorEnc, preProveedorId]);

    useEffect(() => {
        const loadInit = async () => {
            try {
                const result = await CentroService.loadCatalogosIniciales(idProveedorEnc);
                setEstados(result.estados);
                setProveedores(result.proveedores);
                setCategorias(result.categorias);
                setMarcas(result.marcas);
                setTiposCentro(result.tiposCentro);
                setBancos(result.bancos);

                if (result.proveedor) {
                    setFormData((prev) => ({
                        ...prev,
                        rfc: result.proveedor?.rfc ?? prev.rfc,
                        clave_banco: result.proveedor?.clave_banco?.toString() ?? prev.clave_banco,
                        clabe: result.proveedor?.clabe ?? prev.clabe,
                    }));
                }

                if (result.archivosRes.status === 'success' && result.archivosRes.data?.archivos && centroIdEnc) {
                    const claveCentro = decodeProveedorId(centroIdEnc);
                    const allFiles = result.archivosRes.data.archivos;
                    setConstanciaFiles(
                        allFiles.filter(
                            (a) => a.contexto === 'TAX_CERTIFICATE' && String(a.clave_centro) === claveCentro,
                        ) as unknown as EvidenciaImg[],
                    );
                    setCaratulaFiles(
                        allFiles.filter(
                            (a) => a.contexto === 'BANK_COVER' && String(a.clave_centro) === claveCentro,
                        ) as unknown as EvidenciaImg[],
                    );
                }
            } catch {
                setAlert({ type: 'danger', message: 'Error al cargar catálogos.' });
            }
        };

        loadInit();
        if (isEditMode) loadCentroData();
    }, [isEditMode, idProveedorEnc, centroIdEnc, loadCentroData]);

    useEffect(() => {
        // En alta con ?idProveedor=, completar RFC/CLABE si el usuario no los tocó
        if (isEditMode || !formData.clave_persona_proveedor || proveedores.length === 0) return;
        const prov = proveedores.find((p) => String(p.clave_persona_proveedor) === formData.clave_persona_proveedor);
        if (!prov) return;
        setFormData((prev) => ({
            ...prev,
            rfc: prev.rfc || prov.rfc || '',
            banco: prev.banco || prov.banco || '',
            clabe: prev.clabe || prov.clabe || '',
        }));
    }, [isEditMode, formData.clave_persona_proveedor, proveedores]);

    useEffect(() => {
        if (!formData.clave_estado) {
            setCiudades([]);
            return;
        }
        CentroService.loadCiudades(formData.clave_estado)
            .then(setCiudades)
            .catch(() => setCiudades([]));
    }, [formData.clave_estado]);

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
        const { name, value, type } = e.target;
        const checked = (e.target as HTMLInputElement).checked;
        setFormData((prev) => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : type === 'range' ? Number(value) : value,
        }));
    };

    const handleProveedorChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
        const provId = e.target.value;
        const prov = proveedores.find((p) => String(p.clave_persona_proveedor) === provId);
        setFormData((prev) => ({
            ...prev,
            clave_persona_proveedor: provId,
            rfc: prov?.rfc ?? '',
            banco: prov?.banco ?? '',
            clabe: prov?.clabe ?? '',
        }));
    };

    const handleExpedienteChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value, type, checked } = e.target;
        setFormData((prev) => ({
            ...prev,
            expediente: {
                ...prev.expediente,
                [name]: type === 'checkbox' ? checked : value,
            } as ExpedienteCentro,
        }));
    };

    const handleHorarioChange = (id_dia: string, field: keyof HorarioDia, value: string | boolean) => {
        setFormData((prev) => ({
            ...prev,
            horarios: prev.horarios.map((h) => (h.id_dia === id_dia ? { ...h, [field]: value } : h)),
        }));
    };

    const handleAddSlot = (e: React.MouseEvent) => {
        e.preventDefault();
        setSlots((prev) => [...prev, { id: 0, horario: '', cantidad: 1 }]);
    };

    const handleSlotChange = (index: number, field: keyof Slot, value: string | number) => {
        setSlots((prev) => prev.map((slot, i) => (i === index ? { ...slot, [field]: value } : slot)));
    };

    const handleRemoveSlot = (index: number, e: React.MouseEvent) => {
        e.preventDefault();
        if (window.confirm('¿Seguro que desea eliminar este slot?')) {
            setSlots((prev) => prev.filter((_, i) => i !== index));
        }
    };

    const handleStatusChange = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!comentarioEstatus.trim()) {
            setAlert({ type: 'warning', message: 'Debe indicar un motivo.' });
            return;
        }
        if (!window.confirm('¿Confirmar cambio de estatus?')) return;

        setIsChangingEstatus(true);
        try {
            const result = await CentroService.cambiarEstatus({
                id: centroIdEnc,
                idProveedor: btoa(formData.clave_persona_proveedor),
                estatus: nuevoEstatus,
                comentario: comentarioEstatus,
            });
            if (result.status === 'success') {
                setAlert({ type: 'success', message: 'Estatus del centro actualizado.' });
                setFormData((prev) => ({ ...prev, clave_estatus: nuevoEstatus }));
                setComentarioEstatus('');
            } else {
                throw new Error(result.message);
            }
        } catch (err) {
            setAlert({ type: 'danger', message: err instanceof Error ? err.message : 'Error al cambiar estatus.' });
        } finally {
            setIsChangingEstatus(false);
        }
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setIsSaving(true);
        setAlert(null);
        try {
            const endpoint = isEditMode ? '/sittApi/centros/actualizar.php' : '/sittApi/centros/crear.php';
            console.log("formData",formData)
            const payload = CentroService.buildFormPayload(formData, slots, constanciaFiles, caratulaFiles);
            const result = await CentroService.guardarCentro(endpoint, payload);

            if (result.status === 'success') {
                setAlert({ type: 'success', message: 'Guardado correctamente.' });
                window.location.href = `proveedores?accion=edit&id=${result.data?.id_encriptado_prov}`;
            } else {
                throw new Error(result.message);
            }
        } catch (err) {
            setAlert({
                type: 'danger',
                message: err instanceof Error ? err.message : 'Error al guardar.',
            });
        } finally {
            setIsSaving(false);
            window.scrollTo(0, 0);
        }
    };

    const proveedorNombre =
        proveedores.find((p) => String(p.clave_persona_proveedor) === formData.clave_persona_proveedor)
            ?.nombre_completo ?? '';

    return {
        urlParams,
        isEditMode,
        activeTab,
        setActiveTab,
        isLoading,
        isSaving,
        alert,
        setAlert,
        formData,
        slots,
        proveedores,
        proveedorNombre,
        estados,
        ciudades,
        tiposCentro,
        categorias,
        marcas,
        bancos,
        constanciaFiles,
        setConstanciaFiles,
        caratulaFiles,
        setCaratulaFiles,
        nuevoEstatus,
        setNuevoEstatus,
        comentarioEstatus,
        setComentarioEstatus,
        isChangingEstatus,
        mapRef,
        searchInputRef,
        catalogos,
        handleInputChange,
        handleProveedorChange,
        handleExpedienteChange,
        handleHorarioChange,
        handleAddSlot,
        handleSlotChange,
        handleRemoveSlot,
        handleStatusChange,
        handleSubmit,
    };
}
